import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, RotateCw, ImagePlus, Trash2 } from "lucide-react";

/**
 * PhotoActionRow — compact icon+text action strip that sits directly
 * under the photo carousel (and thumbstrip when present). Three actions:
 *
 *  • Replace — swaps the primary photo (parent opens the PhotoEditor
 *    crop dialog on the picked file).
 *  • Delete  — deletes the whole scan (parent opens a confirm dialog).
 *  • Add Photo — appends up to 3 reference photos. Button hides when
 *    the user hits the 3-photo cap.
 *
 * Design intent: minimal styling (transparent background, icon +
 * label, generous spacing) so the row reads as an affordance rather
 * than another visual card. Mirrors the "Replace / Delete / Add Photo"
 * row pattern users recognize from other plant apps.
 */
export default function PhotoActionRow({
  replacing,
  addingPhoto,
  deleting,
  extraCount,
  maxExtras = 3,
  onPickReplacement,
  onPickExtraPhoto,
  onRequestDeleteScan,
}) {
  const { t } = useTranslation();
  const replaceInputRef = useRef(null);
  const extraInputRef = useRef(null);
  const canAddMore = extraCount < maxExtras;

  return (
    <div
      className="mx-5 mt-3 flex items-center justify-center gap-2 sm:gap-6"
      data-testid="photo-action-row"
    >
      <button
        type="button"
        onClick={() => replaceInputRef.current?.click()}
        disabled={replacing}
        data-testid="replace-photo-button"
        aria-label={t("result.replacePhotoAria")}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-leaf hover:bg-parchment disabled:opacity-60 transition text-sm font-medium"
      >
        {replacing
          ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
          : <RotateCw className="w-4 h-4" strokeWidth={1.75} />}
        <span>{replacing ? t("result.replacing") : t("result.replacePhoto")}</span>
      </button>

      {typeof onRequestDeleteScan === "function" && (
        <button
          type="button"
          onClick={onRequestDeleteScan}
          disabled={deleting}
          data-testid="delete-scan-button"
          aria-label={t("result.deleteScanAria")}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-rust hover:bg-[#FDECE4] disabled:opacity-60 transition text-sm font-medium"
        >
          {deleting
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
            : <Trash2 className="w-4 h-4" strokeWidth={1.75} />}
          <span>{t("result.deleteScan")}</span>
        </button>
      )}

      {canAddMore && (
        <button
          type="button"
          onClick={() => extraInputRef.current?.click()}
          disabled={addingPhoto}
          data-testid="add-extra-photo"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-leaf hover:bg-parchment disabled:opacity-60 transition text-sm font-medium"
        >
          {addingPhoto
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
            : <ImagePlus className="w-4 h-4" strokeWidth={1.75} />}
          <span>{t("result.addPhoto", { count: extraCount, max: maxExtras })}</span>
        </button>
      )}

      {/* Hidden inputs — both actions use the OS file chooser. Kept
          alongside their triggers so the ref/click wiring stays local. */}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onPickReplacement}
        className="hidden"
        data-testid="replace-photo-input"
      />
      <input
        ref={extraInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onPickExtraPhoto}
        className="hidden"
        data-testid="extra-photo-input"
      />
    </div>
  );
}
